import { apiBaseUrl, apiGet, apiPost, type ApiEnvelope } from './client.js';
import { useAuthStore } from '@/stores/auth.js';

export type MigrationJobType =
  | 'customers'
  | 'inventory'
  | 'inventory_bulk'
  | 'services'
  | 'ticket_history'
  | 'invoice_history';

export type MigrationJobStatus =
  | 'uploaded'
  | 'validating'
  | 'validated'
  | 'invalid'
  | 'importing'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'pending'
  | 'processing'
  | 'running';

export type MigrationLogEntry = {
  at: string;
  level: 'info' | 'warn' | 'error';
  message: string;
};

export type MigrationValidationError =
  | string
  | { row?: number; message: string; field?: string };

export type MigrationValidationResult = {
  valid: boolean;
  totalRows: number;
  preview: Array<Record<string, unknown>>;
  errors: MigrationValidationError[];
  warnings: string[];
  summary?: Record<string, number | undefined>;
};

export type MigrationJob = {
  id: string;
  jobType: MigrationJobType | string;
  status: MigrationJobStatus | string;
  totalRows: number;
  processedRows: number;
  importedCount: number;
  skippedCount: number;
  errorCount: number;
  log: MigrationLogEntry[];
  fileName: string | null;
  fileSize: number | null;
  options: Record<string, unknown>;
  validationResult: MigrationValidationResult | null;
  lastProcessedIndex: number;
  errorMessage: string | null;
  createdAt: string;
  startedAt: string | null;
  updatedAt: string | null;
  completedAt: string | null;
};

export type CustomersOnDuplicate = 'skip' | 'update';
export type InventoryOnDuplicate = 'skip' | 'update';
export type ServicesOnDuplicate = 'skip' | 'update_pricing' | 'skip_pricing';

export type InvoiceOnDuplicate = 'skip' | 'update';

export type MigrationImportOptions = {
  onDuplicate?: CustomersOnDuplicate | InventoryOnDuplicate | ServicesOnDuplicate | InvoiceOnDuplicate;
  createMissingCategories?: boolean;
  createMissingSuppliers?: boolean;
  createMissingDevices?: boolean;
  ticketPrefix?: string;
  salePrefix?: string;
  skipDuplicates?: boolean;
  skipMissingTickets?: boolean;
  allowTrackInventory?: boolean;
  allowCondition?: boolean;
  allowArchived?: boolean;
};

const ACTIVE_JOB_KEY = 'fixflow_active_migration_job';

export function saveActiveMigrationJobId(jobId: string): void {
  try {
    localStorage.setItem(ACTIVE_JOB_KEY, jobId);
  } catch {
    /* ignore */
  }
}

export function loadActiveMigrationJobId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_JOB_KEY);
  } catch {
    return null;
  }
}

export function clearActiveMigrationJobId(): void {
  try {
    localStorage.removeItem(ACTIVE_JOB_KEY);
  } catch {
    /* ignore */
  }
}

export function isMigrationJobInProgress(status: string): boolean {
  return ['uploaded', 'validating', 'importing', 'pending', 'processing', 'running'].includes(
    status,
  );
}

export function formatValidationError(err: MigrationValidationError): string {
  if (typeof err === 'string') return err;
  const row = err.row != null ? `Row ${err.row}: ` : '';
  return `${row}${err.message}`;
}

export function validationRowCount(job: MigrationJob | null | undefined): number {
  return (
    job?.validationResult?.totalRows ??
    job?.validationResult?.summary?.total ??
    job?.totalRows ??
    0
  );
}

async function authMultipart(path: string, form: FormData): Promise<ApiEnvelope<{ jobId: string }>> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`${apiBaseUrl}${path}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: form,
  });
  const json = (await res.json().catch(() => ({}))) as ApiEnvelope<{ jobId: string }> & {
    errors?: Array<{ message?: string; code?: string }>;
  };
  if (!res.ok) {
    const message = json.errors?.[0]?.message ?? `Upload failed (${res.status})`;
    throw Object.assign(new Error(message), { response: { status: res.status, data: json } });
  }
  return json;
}

export async function uploadMigrationFile(
  file: File,
  jobType: MigrationJobType,
): Promise<{ jobId: string }> {
  const form = new FormData();
  form.append('file', file);
  form.append('jobType', jobType);
  const res = await authMultipart('/api/v1/migration/upload', form);
  return res.data;
}

export async function startMigrationValidate(
  jobId: string,
  options?: MigrationImportOptions,
): Promise<{ jobId: string; status: string }> {
  const res = await apiPost<{ jobId: string; status: string }>(
    `/api/v1/migration/${jobId}/validate`,
    options ?? {},
  );
  return res.data;
}

export async function startMigrationImport(
  jobId: string,
  options: MigrationImportOptions,
): Promise<{ jobId: string; status: string }> {
  const res = await apiPost<{ jobId: string; status: string }>(
    `/api/v1/migration/${jobId}/import`,
    options,
  );
  return res.data;
}

export async function resumeMigrationImport(
  jobId: string,
): Promise<{ jobId: string; status: string; lastProcessedIndex?: number }> {
  const res = await apiPost<{ jobId: string; status: string; lastProcessedIndex?: number }>(
    `/api/v1/migration/${jobId}/resume`,
  );
  return res.data;
}

export async function cancelMigrationJob(jobId: string): Promise<{ jobId: string; status: string }> {
  const res = await apiPost<{ jobId: string; status: string }>(
    `/api/v1/migration/${jobId}/cancel`,
  );
  return res.data;
}

export async function getMigrationJob(jobId: string): Promise<MigrationJob> {
  const res = await apiGet<MigrationJob>(`/api/v1/migration/${jobId}`);
  return res.data;
}
